import { useDocsDataModelStore } from '@/models/docs-data/docsData';
import { getRequestBaseUrl, getWebsocketRequestBaseUrl } from '@/shared/util/requestBaseUrl';
import { ModelType } from '@tarslib/types/lib/common';
import { EnvironmentMockType } from '@tarslib/types/lib/environment';
import type { SelectProps } from 'aui';
import { Select } from 'aui';
import { FoxIconGlobe1 } from 'aui-biz-icons';
import clsx from 'clsx';
import React from 'react';
import styles from './EnvironmentSelect.module.css';

export type EnvironmentSelectProps = Pick<SelectProps, 'size' | 'bordered' | 'variant'> & {
  className?: string;
  selectClassName?: string;
};

export function EnvironmentSelect(props: EnvironmentSelectProps) {
  const { bordered, size, className, selectClassName, variant } = props;

  const environmentList = useDocsDataModelStore((x) => x.environments.environmentList);
  const selectedEnvironment = useDocsDataModelStore((x) => x.environments.selectedEnvironment);
  const selectEnvironment = useDocsDataModelStore((x) => x.selectEnvironment);
  const servers = useDocsDataModelStore((x) => x.environments.servers);
  const serverId = useDocsDataModelStore((x) => {
    if (
      x.resourceData.type !== ModelType.HttpApi &&
      x.resourceData.type !== ModelType.WebSocketApi
    ) {
      return '';
    }
    return x.resourceData.extraData.serverId;
  });
  const resourceType = useDocsDataModelStore((x) => x.resourceData.type);

  const options = React.useMemo(() => {
    return environmentList.reduce<
      {
        label: React.ReactNode;
        value: number;
      }[]
    >((acc, x) => {
      if (
        resourceType === ModelType.WebSocketApi &&
        x.type &&
        EnvironmentMockType.includes(x.type)
      ) {
        return acc;
      }

      const baseUrl =
        resourceType === ModelType.WebSocketApi
          ? getWebsocketRequestBaseUrl({
              serverId,
              environment: x,
            })
          : getRequestBaseUrl({
              serverId,
              environment: x,
              servers,
            });

      const option = {
        label: (
          <div className={clsx('flex items-center truncate')}>
            <div className={clsx('flex items-center', styles['select-label-name'])} title={x.name}>
              <FoxIconGlobe1
                className={clsx('mr-1.5', {
                  ['text-input']: size === 'small',
                  ['text-input-icon']: size !== 'small',
                })}
              />
              <div
                className={clsx('truncate', {
                  ['mr-1']: size !== 'small',
                })}
              >
                <div className="truncate">{x.name}</div>
              </div>
            </div>

            {!!baseUrl && (
              <div
                className={clsx(
                  styles['select-label-detail'],
                  'flex w-0 min-w-[250px] flex-1 justify-between pl-2 text-sm font-400 text-color-tertiary',
                )}
                title={baseUrl}
              >
                <div className="flex-1" />
                <div className="flex items-center truncate text-right">
                  <div className="truncate">{baseUrl}</div>
                </div>
              </div>
            )}
          </div>
        ),
        value: x.id,
      };

      acc.push(option);
      return acc;
    }, []);
  }, [environmentList, serverId, servers, size, resourceType]);

  return (
    <div className={clsx(className)}>
      <Select
        className={clsx(selectClassName, styles.select)}
        popupClassName={styles.popup}
        size={size}
        options={options}
        value={selectedEnvironment?.id}
        onChange={(e) => selectEnvironment(e)}
        bordered={bordered}
        variant={variant}
        dropdownMatchSelectWidth={false}
      />
    </div>
  );
}
